// PDF export for Get Who To Buy Strategy using html2canvas
// Renders beautiful HTML slides and converts them to PDF
import html2canvas from 'html2canvas';
import { PDFDocument } from 'pdf-lib';

// Helper to strip markdown links for display
const stripMarkdownLinks = (text) => {
  if (!text) return '';
  return text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Replace [text](url) with text
    .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold **text**
    .replace(/\*([^*]+)\*/g, '$1'); // Remove italic *text*
};

// Helper to create HTML slide element with proper styling
const createSlideHTML = (content, theme = 'dark') => {
  const isDark = theme === 'dark';
  const bgMain = isDark ? '#000000' : '#FFFFFF';
  const textMain = isDark ? '#FFFFFF' : '#000000';
  const textMuted = isDark ? '#A1A1AA' : '#52525B';
  const textBody = isDark ? '#D4D4D8' : '#27272A';
  const bgCard = isDark ? '#18181B' : '#F4F4F5';
  const borderCard = isDark ? '#27272A' : '#D4D4D8';
  const textBodyCard = isDark ? '#E4E4E7' : '#27272A';
  const textNumber = isDark ? '#71717A' : '#A1A1AA';

  return {
    html: `
      <div style="
        width: 1920px;
        height: 1080px;
        background: ${bgMain};
        color: ${textMain};
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        padding: 120px 96px;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        justify-content: center;
        position: relative;
        overflow: hidden;
      ">
        ${content}
      </div>
    `,
    colors: { textMuted, textBody, bgCard, borderCard, textBodyCard, textNumber }
  };
};

// Render all slides as HTML and convert to PDF
export async function exportGetWhoToBuyPdf(strategyData, fileName = 'get-who-to-buy-strategy.pdf') {
  // Create a temporary container for rendering slides
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = '1920px';
  container.style.height = '1080px';
  container.style.overflow = 'hidden';
  document.body.appendChild(container);

  try {
    // Slide 1: Title
    const titleSlideColors = createSlideHTML('').colors;
    const titleSlideHTML = createSlideHTML(`
      <div style="text-align: center; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%;">
        <h1 style="font-size: 120px; font-weight: 700; margin: 0 0 32px 0; letter-spacing: -0.02em; line-height: 1; color: #FFFFFF;">ASKPOT</h1>
        <p style="font-size: 32px; color: ${
          titleSlideColors.textMuted
        }; letter-spacing: 0.1em; text-transform: uppercase; margin: 0; font-weight: 500;">
          ${strategyData.client ? `Strategic Framework for ${strategyData.client}` : 'Strategic Brief 2025'}
        </p>
        ${
          strategyData.category
            ? `<p style="font-size: 20px; color: ${titleSlideColors.textNumber}; margin-top: 24px; font-weight: 400;">${strategyData.category}</p>`
            : ''
        }
      </div>
    `).html;

    // Slide 2-5: GET / WHO / TO / BY
    const frameworkColors = createSlideHTML('').colors;
    const frameworkSlides = [
      { label: 'GET', content: strategyData.get },
      { label: 'WHO', content: strategyData.who },
      { label: 'TO', content: strategyData.to },
      { label: 'BY', content: strategyData.by }
    ]
      .filter((s) => s.content)
      .map((section) => {
        const content = stripMarkdownLinks(section.content);
        return createSlideHTML(`
        <div style="max-width: 1400px; margin: 0 auto;">
          <h2 style="font-size: 96px; font-weight: 700; margin: 0 0 64px 0; letter-spacing: -0.02em; line-height: 1; color: #FFFFFF;">${section.label}</h2>
          <p style="font-size: 32px; line-height: 1.7; color: ${frameworkColors.textBody}; font-weight: 400;">
            ${content}
          </p>
        </div>
      `).html;
      });

    // Slide 6: Key Strategic Insight
    let insightSlideHTML = null;
    if (strategyData.key_strategic_insight) {
      const content = stripMarkdownLinks(strategyData.key_strategic_insight);
      const insightColors = createSlideHTML('').colors;
      insightSlideHTML = createSlideHTML(`
        <div style="max-width: 1400px; margin: 0 auto;">
          <h2 style="font-size: 80px; font-weight: 700; margin: 0 0 48px 0; letter-spacing: -0.02em; color: #FFFFFF;">Key Strategic Insight</h2>
          <div style="background: ${insightColors.bgCard}; border: 1px solid ${insightColors.borderCard}; padding: 64px; border-radius: 16px;">
            <p style="font-size: 28px; line-height: 1.8; color: ${insightColors.textBodyCard}; font-weight: 400;">
              ${content}
            </p>
          </div>
        </div>
      `).html;
    }

    // Slide 7: Creative Implication
    let creativeSlideHTML = null;
    if (strategyData.creative_implication) {
      const content = stripMarkdownLinks(strategyData.creative_implication);
      const creativeColors = createSlideHTML('').colors;
      creativeSlideHTML = createSlideHTML(`
        <div style="max-width: 1400px; margin: 0 auto;">
          <h2 style="font-size: 80px; font-weight: 700; margin: 0 0 48px 0; letter-spacing: -0.02em; color: #FFFFFF;">Creative Implication</h2>
          <p style="font-size: 28px; line-height: 1.8; color: ${creativeColors.textBody}; font-weight: 400; margin-bottom: 32px;">
            ${content}
          </p>
        </div>
      `).html;
    }

    // Slide 8+: Supporting Data Points
    const dataPointSlides = [];
    if (strategyData.supporting_data_points && strategyData.supporting_data_points.length > 0) {
      const itemsPerSlide = 4;
      const totalSlides = Math.ceil(strategyData.supporting_data_points.length / itemsPerSlide);

      for (let i = 0; i < totalSlides; i++) {
        const startIdx = i * itemsPerSlide;
        const pageItems = strategyData.supporting_data_points.slice(startIdx, startIdx + itemsPerSlide);

        const dataColors = createSlideHTML('').colors;
        const itemsHTML = pageItems
          .map((item, idx) => {
            const content = stripMarkdownLinks(item);
            return `
            <div style="display: flex; gap: 32px; margin-bottom: 40px; align-items: flex-start;">
              <div style="font-size: 56px; font-weight: 700; color: ${dataColors.textNumber}; min-width: 80px; line-height: 1;">${String(
              startIdx + idx + 1
            ).padStart(2, '0')}</div>
              <p style="font-size: 22px; line-height: 1.7; color: ${dataColors.textBody}; flex: 1; margin: 0; font-weight: 400;">
                ${content}
              </p>
            </div>
          `;
          })
          .join('');

        dataPointSlides.push(
          createSlideHTML(`
          <div style="max-width: 1400px; margin: 0 auto;">
            <h2 style="font-size: 80px; font-weight: 700; margin: 0 0 56px 0; letter-spacing: -0.02em; color: #FFFFFF;">Supporting Data Points</h2>
            ${itemsHTML}
          </div>
        `).html
        );
      }
    }

    // Slide 9+: Tone & Channel Direction
    const toneSlides = [];
    if (strategyData.tone_channel_direction) {
      const entries = Object.entries(strategyData.tone_channel_direction).filter((e) => e[1]);
      const itemsPerSlide = 2;
      const totalSlides = Math.ceil(entries.length / itemsPerSlide);

      for (let i = 0; i < totalSlides; i++) {
        const startIdx = i * itemsPerSlide;
        const pageEntries = entries.slice(startIdx, startIdx + itemsPerSlide);

        const toneColors = createSlideHTML('').colors;
        const itemsHTML = pageEntries
          .map(([key, value]) => {
            const title = key
              .split('_')
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(' ');
            const content = stripMarkdownLinks(value);
            return `
            <div style="background: ${toneColors.bgCard}; border: 1px solid ${toneColors.borderCard}; padding: 40px; border-radius: 12px; margin-bottom: 32px;">
              <h3 style="font-size: 28px; font-weight: 700; margin: 0 0 20px 0; color: #1E88E5;">${title}</h3>
              <p style="font-size: 20px; line-height: 1.7; color: ${toneColors.textBodyCard}; margin: 0; font-weight: 400;">
                ${content}
              </p>
            </div>
          `;
          })
          .join('');

        toneSlides.push(
          createSlideHTML(`
          <div style="max-width: 1400px; margin: 0 auto;">
            <h2 style="font-size: 80px; font-weight: 700; margin: 0 0 56px 0; letter-spacing: -0.02em; color: #FFFFFF;">Tone & Channel Direction</h2>
            ${itemsHTML}
          </div>
        `).html
        );
      }
    }

    // Slide 10+: Strategic Guardrails
    const guardrailSlides = [];
    if (strategyData.strategic_guardrails) {
      const entries = Object.entries(strategyData.strategic_guardrails).filter((e) => e[1]);
      const itemsPerSlide = 2;
      const totalSlides = Math.ceil(entries.length / itemsPerSlide);

      for (let i = 0; i < totalSlides; i++) {
        const startIdx = i * itemsPerSlide;
        const pageEntries = entries.slice(startIdx, startIdx + itemsPerSlide);

        const guardrailColors = createSlideHTML('').colors;
        const itemsHTML = pageEntries
          .map(([key, value]) => {
            const title = key
              .split('_')
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(' ');
            const content = stripMarkdownLinks(value);
            return `
            <div style="border-left: 4px solid #D32F2F; padding-left: 40px; margin-bottom: 40px;">
              <h3 style="font-size: 36px; font-weight: 700; margin: 0 0 20px 0; line-height: 1.2; color: #FFFFFF;">${title}</h3>
              <p style="font-size: 22px; line-height: 1.7; color: ${guardrailColors.textBody}; margin: 0; font-weight: 400;">
                ${content}
              </p>
            </div>
          `;
          })
          .join('');

        guardrailSlides.push(
          createSlideHTML(`
          <div style="max-width: 1400px; margin: 0 auto;">
            <h2 style="font-size: 80px; font-weight: 700; margin: 0 0 56px 0; letter-spacing: -0.02em; color: #FFFFFF;">Strategic Guardrails</h2>
            ${itemsHTML}
          </div>
        `).html
        );
      }
    }

    // Combine all slides
    const allSlidesHTML = [
      titleSlideHTML,
      ...frameworkSlides,
      insightSlideHTML,
      creativeSlideHTML,
      ...dataPointSlides,
      ...toneSlides,
      ...guardrailSlides
    ].filter(Boolean);

    // Create PDF document
    const pdfDoc = await PDFDocument.create();

    // Convert each HTML slide to image and add to PDF
    for (let i = 0; i < allSlidesHTML.length; i++) {
      const slideHTML = allSlidesHTML[i];

      // Create slide element
      const wrapper = document.createElement('div');
      wrapper.innerHTML = slideHTML;
      const slideElement = wrapper.firstElementChild;

      if (!slideElement) continue;

      // Ensure proper styling
      slideElement.style.position = 'relative';
      slideElement.style.display = 'block';
      slideElement.style.visibility = 'visible';
      slideElement.style.opacity = '1';
      slideElement.style.width = '1920px';
      slideElement.style.height = '1080px';

      // Force all text elements to have explicit colors
      const allTextElements = slideElement.querySelectorAll('h1, h2, h3, p, div, span');
      allTextElements.forEach((el) => {
        const computedColor = window.getComputedStyle(el).color;
        if (computedColor && computedColor !== 'rgba(0, 0, 0, 0)' && computedColor !== 'transparent') {
          el.style.setProperty('color', computedColor, 'important');
        }
        el.style.setProperty('visibility', 'visible', 'important');
        el.style.setProperty('opacity', '1', 'important');
      });

      container.appendChild(slideElement);

      // Wait for rendering
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Capture with html2canvas
      const canvas = await html2canvas(slideElement, {
        width: 1920,
        height: 1080,
        scale: 2,
        useCORS: true,
        backgroundColor: '#000000',
        logging: false,
        windowWidth: 1920,
        windowHeight: 1080,
        allowTaint: false
      });

      // Convert to image data
      const imageData = canvas.toDataURL('image/png');
      const imageBytes = await fetch(imageData).then((res) => res.arrayBuffer());

      // Add page to PDF (landscape: 1920x1080 points = 16:9 ratio)
      // Convert pixels to points: 1px = 0.75pt (at 96 DPI)
      const page = pdfDoc.addPage([1440, 810]); // 1920*0.75 = 1440, 1080*0.75 = 810

      // Embed image
      const image = await pdfDoc.embedPng(imageBytes);

      // Add image to page (scale to fit)
      page.drawImage(image, {
        x: 0,
        y: 0,
        width: 1440,
        height: 810
      });

      // Remove slide element
      container.removeChild(slideElement);
    }

    // Save PDF
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } finally {
    // Clean up
    document.body.removeChild(container);
  }
}

export default exportGetWhoToBuyPdf;
